import { createClient } from "@supabase/supabase-js";
import * as pdfjs from "pdfjs-dist";
import mammoth from "mammoth";
import * as XLSX from "xlsx";
import { verifyEmail, autoVerifyContact } from "./services/emailVerification";
import { geocodeAddress } from "./services/geocoding";
import { addressKey } from "./utils/addressKey";
import { parseDealName } from "./utils/parseDealName";

// ─── County + MSA auto-tag helper ────────────────────────────────────────
// Dynamic import breaks the circular between this file and services/countyLookup
// (countyLookup reads msa_definitions via `supabase` from here). Returns
// { counties, msas, marketString } — pass any of these into the DB update
// payload for a fresh auto-tag on every address change.
async function _tagAddresses(addresses) {
  const list = (addresses || []).map(a => (a || "").toString().trim()).filter(Boolean);
  if (list.length === 0) return { counties: [], msas: [], marketString: "" };
  try {
    const mod = await import("./services/countyLookup");
    return await mod.resolveCountiesAndMsas(list);
  } catch (e) {
    console.warn("_tagAddresses failed:", e.message);
    return { counties: [], msas: [], marketString: "" };
  }
}

// ─── Buy-box auto-tag helper ─────────────────────────────────────────────
// Runs the current buy-box rule against a candidate row and returns the
// buy_box_* columns you should merge into the DB payload. Same dynamic-
// import pattern as _tagAddresses to keep the module graph acyclic.
//
// The `row` argument should already carry the tag output (msas, market)
// AND the payload fields the evaluator wants to read — i.e. call
// _gradeBuyBox AFTER _tagAddresses so the fresh MSAs are visible.
async function _gradeBuyBox(row) {
  try {
    const [rulesMod, evalMod] = await Promise.all([
      import("./services/buyBoxRules"),
      import("./services/buyBoxEvaluator"),
    ]);
    const { rule, version } = await rulesMod.getActiveBuyBox();
    const { verdict, reason } = evalMod.evaluateBuyBox(rule, row);
    return {
      buy_box_verdict: verdict,
      buy_box_source: "auto",
      buy_box_reason: reason,
      buy_box_version: version,
      buy_box_updated_at: new Date().toISOString(),
    };
  } catch (e) {
    console.warn("_gradeBuyBox failed:", e.message);
    return null;
  }
}

// Point pdf.js at a CDN-hosted worker so we don't have to copy the worker file
// into the public folder. Must match the version of pdfjs-dist we installed.
pdfjs.GlobalWorkerOptions.workerSrc =
  "https://unpkg.com/pdfjs-dist@5.6.205/build/pdf.worker.min.mjs";

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn(
    "Supabase env vars not set. Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to .env"
  );
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// ─── Data helpers ─────────────────────────────────────────────────────────────
// Supabase uses snake_case columns; the app uses camelCase. These two functions
// convert between them so the rest of the app never touches raw DB field names.

export function dbToDeal(row) {
  // address column stores either a JSON array string or a legacy plain string
  let addresses;
  try {
    const parsed = JSON.parse(row.address ?? "");
    addresses = Array.isArray(parsed) ? parsed : [row.address ?? ""];
  } catch {
    addresses = [row.address ?? ""];
  }

  return {
    id: row.id,
    addresses,
    market: row.market,
    msas: row.msas || [],
    counties: row.counties || [],
    squareFeet: row.square_footage,
    askingPrice: row.asking_price,
    capRate: row.cap_rate,
    stage: row.deal_stage,
    sourceNotes: row.source_notes ?? "",
    photoPath: row.photo_path ?? "",
    // The automatic Street View frame. Deliberately a SEPARATE field from
    // photoPath: a picture somebody chose always wins, and a capture run
    // can never quietly replace it. See migrations/026.
    streetViewPath: row.street_view_path ?? "",
    thesis: row.thesis ?? "",
    openQuestions: row.open_questions ?? "",
    redFlags: row.red_flags ?? "",
    underwrittenPrice: row.underwritten_price ?? null,
    leveredIrr:        row.levered_irr        ?? null,
    equityMultiple:    row.equity_multiple    ?? null,
    trendedYoc:        row.trended_yoc        ?? null,
    untrendedYoc:      row.untrended_yoc      ?? null,
    averageCoc:        row.average_coc        ?? null,
    latitude:          row.latitude           ?? null,
    longitude:         row.longitude          ?? null,
    marketed:          row.marketed           ?? false,
    callForOffersDate: row.call_for_offers_date ?? null,
    createdAt: row.created_at,
    productType:       row.product_type        ?? null,
    yearBuilt:         row.year_built          ?? null,
    clearHeight:       row.clear_height        ?? null,
    opexPsf:           row.opex_psf            ?? null,   // annual $/SF
    killedAt:                  row.killed_at                  ?? null,
    killedReason:              row.killed_reason              ?? null,
    killedNarrative:           row.killed_narrative           ?? null,
    brokerName:                row.broker_name                ?? null,
    brokerFirm:                row.broker_firm                ?? null,
    brokerEmail:               row.primary_broker             ?? null,
    brokerPhone:               row.broker_phone               ?? null,
    // Outreach section (migration 036): an LOI submitted through a broker.
    dealFlow:                  row.deal_flow                  ?? "pipeline",
    offerPrice:                row.offer_price                ?? null,
    loiDate:                   row.loi_date                   ?? null,
    loiDeposit:                row.loi_deposit                ?? null,
    loiDdDays:                 row.loi_dd_days                ?? null,
    loiClosingDays:            row.loi_closing_days           ?? null,
    loiTerms:                  row.loi_terms                  ?? null,
    followUpDate:              row.follow_up_date             ?? null,
    confirmedReceiptAt:        row.confirmed_receipt_at       ?? null,
    counteredAt:               row.countered_at               ?? null,
    counterPrice:              row.counter_price              ?? null,
    declinedAt:                row.declined_at                ?? null,
    promotedAt:                row.promoted_at                ?? null,
    madeBestAndFinal:          row.made_best_and_final        ?? false,
    marketClearedPrice:        row.market_cleared_price       ?? null,
    marketClearedPricePsf:     row.market_cleared_price_psf   ?? null,
    marketClearedBuyer:        row.market_cleared_buyer       ?? null,
    marketClearedAt:           row.market_cleared_at          ?? null,
    marketClearedNotes:        row.market_cleared_notes       ?? null,
    timeToLoiDays:             row.time_to_loi_days           ?? null,
    timeInUwDays:              row.time_in_uw_days            ?? null,
    buyBoxVerdict:     row.buy_box_verdict     ?? "undecided",
    buyBoxSource:      row.buy_box_source      ?? "auto",
    buyBoxReason:      row.buy_box_reason      ?? null,
    buyBoxVersion:     row.buy_box_version     ?? null,
    buyBoxUpdatedAt:   row.buy_box_updated_at  ?? null,
  };
}

export function dealToDb(deal) {
  return {
    // store addresses as a JSON array string
    address: JSON.stringify(deal.addresses ?? []),
    market: deal.market,
    square_footage: deal.squareFeet,
    asking_price: deal.askingPrice,
    cap_rate: deal.capRate || 0,
    deal_stage: deal.stage,
    source_notes: deal.sourceNotes,
    photo_path: deal.photoPath ?? "",
    thesis: deal.thesis,
    open_questions: deal.openQuestions,
    red_flags: deal.redFlags,
    underwritten_price: deal.underwrittenPrice || null,
    levered_irr:        deal.leveredIrr        || null,
    equity_multiple:    deal.equityMultiple    || null,
    trended_yoc:        deal.trendedYoc        || null,
    untrended_yoc:      deal.untrendedYoc      || null,
    average_coc:        deal.averageCoc        || null,
    marketed:           deal.marketed          ?? false,
    call_for_offers_date: deal.callForOffersDate || null,
    product_type:       deal.productType       || null,
    year_built:         deal.yearBuilt         || null,
    clear_height:       deal.clearHeight       || null,
    // Nullish, not falsy — a genuine 0 OPEX is a real value, not "unset".
    opex_psf:           deal.opexPsf ?? null,
    killed_at:                  deal.killedAt                  || null,
    killed_reason:              deal.killedReason              || null,
    killed_narrative:           deal.killedNarrative           || null,
    broker_name:                deal.brokerName                || null,
    broker_firm:                deal.brokerFirm                || null,
    primary_broker:             deal.brokerEmail               || null,
    broker_phone:               deal.brokerPhone               || null,
    deal_flow:                  deal.dealFlow                  || "pipeline",
    offer_price:                deal.offerPrice                ?? null,
    loi_date:                   deal.loiDate                   || null,
    loi_deposit:                deal.loiDeposit                ?? null,
    loi_dd_days:                deal.loiDdDays                 ?? null,
    loi_closing_days:           deal.loiClosingDays            ?? null,
    loi_terms:                  deal.loiTerms                  || null,
    follow_up_date:             deal.followUpDate              || null,
    confirmed_receipt_at:       deal.confirmedReceiptAt        || null,
    countered_at:               deal.counteredAt               || null,
    counter_price:              deal.counterPrice              ?? null,
    declined_at:                deal.declinedAt                || null,
    promoted_at:                deal.promotedAt                || null,
    made_best_and_final:        deal.madeBestAndFinal          ?? false,
    market_cleared_price:       deal.marketClearedPrice        || null,
    market_cleared_price_psf:   deal.marketClearedPricePsf     || null,
    market_cleared_buyer:       deal.marketClearedBuyer        || null,
    market_cleared_at:          deal.marketClearedAt           || null,
    market_cleared_notes:       deal.marketClearedNotes        || null,
  };
}

// ─── Message helpers ──────────────────────────────────────────────────────────

// Fetch all chat messages for a deal, oldest first.
// Returns an array of { id, deal_id, role, content, created_at } rows.
export async function loadMessages(dealId) {
  const { data, error } = await supabase
    .from("messages")
    .select("*")
    .eq("deal_id", dealId)
    .order("created_at", { ascending: true });

  if (error) {
    console.error("loadMessages error:", error.message);
    return [];
  }
  return data;
}

// Persist a single chat message. Fire-and-forget — errors are logged, not thrown.
export async function saveMessage(dealId, role, content) {
  const { error } = await supabase
    .from("messages")
    .insert([{ deal_id: dealId, role, content }]);

  if (error) {
    console.error("saveMessage error:", error.message);
  }
}

// ─── Comp helpers ─────────────────────────────────────────────────────────────

export async function loadSaleComps(dealId) {
  const { data, error } = await supabase
    .from("sale_comps")
    .select("*")
    .eq("deal_id", dealId)
    .order("created_at", { ascending: false });

  if (error) { console.error("loadSaleComps error:", error.message); return []; }
  return data;
}

export async function loadLeaseComps(dealId) {
  const { data, error } = await supabase
    .from("lease_comps")
    .select("*")
    .eq("deal_id", dealId)
    .order("created_at", { ascending: false });

  if (error) { console.error("loadLeaseComps error:", error.message); return []; }
  return data;
}

// comp object keys must match DB columns (address, submarket, sf, sale_price, etc.)
export async function saveSaleComp(dealId, comp) {
  const { data, error } = await supabase
    .from("sale_comps")
    .insert([{ deal_id: dealId, ...comp }])
    .select()
    .single();

  if (error) { console.error("saveSaleComp error:", error.message); return null; }
  return data;
}

export async function saveLeaseComp(dealId, comp) {
  const { data, error } = await supabase
    .from("lease_comps")
    .insert([{ deal_id: dealId, ...comp }])
    .select()
    .single();

  if (error) { console.error("saveLeaseComp error:", error.message); return null; }
  return data;
}

export async function deleteSaleComp(compId) {
  const { error } = await supabase.from("sale_comps").delete().eq("id", compId);
  if (error) console.error("deleteSaleComp error:", error.message);
}

export async function deleteLeaseComp(compId) {
  const { error } = await supabase.from("lease_comps").delete().eq("id", compId);
  if (error) console.error("deleteLeaseComp error:", error.message);
}

// ─── Deal Comp Set helpers ────────────────────────────────────────────────────
// The new "assigned comps" model: each deal can pull rows from the global
// comps library AND keep its own subject-property rent roll. See
// scripts/2026-05-29-deal-comp-set-phase1.sql for the schema.

// Returns every comp assigned to a deal — JOINed with the global comps row
// so callers get full comp data + assignment metadata in one shot.
export async function loadAssignedComps(dealId) {
  const { data, error } = await supabase
    .from("deal_assigned_comps")
    .select("id, added_via, notes, created_at, created_by, comp:comps(*)")
    .eq("deal_id", dealId)
    .order("created_at", { ascending: false });
  if (error) { console.error("loadAssignedComps error:", error.message); return []; }
  // Flatten: { ...comp, _assignmentId, _addedVia, ... } so consumers can treat
  // them as comp rows with extra metadata attached.
  return (data || []).map(row => ({
    ...row.comp,
    _assignmentId: row.id,
    _addedVia: row.added_via,
    _assignmentNotes: row.notes,
    _assignedAt: row.created_at,
    _assignedBy: row.created_by,
  })).filter(r => r.id);  // drop any rows where the JOIN couldn't find the comp
}

// Assigns one global-library comp to a deal. Idempotent — re-assigning the
// same comp returns the existing assignment row instead of erroring.
export async function assignCompToDeal(dealId, compId, addedVia = "library_picker", notes = null, createdBy = null) {
  const { data, error } = await supabase
    .from("deal_assigned_comps")
    .upsert([{ deal_id: dealId, comp_id: compId, added_via: addedVia, notes, created_by: createdBy }], {
      onConflict: "deal_id,comp_id",
      ignoreDuplicates: false,
    })
    .select()
    .single();
  if (error) { console.error("assignCompToDeal error:", error.message); return null; }
  return data;
}

// Bulk version of assignCompToDeal. Returns { count, error } so callers can
// surface failures (e.g. RLS / schema issues) instead of silently seeing 0
// new assignments. Already-assigned comps are silently skipped per D5.
export async function assignCompsToDeal(dealId, compIds, addedVia = "library_picker", createdBy = null) {
  if (!compIds || compIds.length === 0) return { count: 0, error: null };
  const rows = compIds.map(comp_id => ({ deal_id: dealId, comp_id, added_via: addedVia, created_by: createdBy }));
  const { data, error } = await supabase
    .from("deal_assigned_comps")
    .upsert(rows, { onConflict: "deal_id,comp_id", ignoreDuplicates: true })
    .select();
  if (error) { console.error("assignCompsToDeal error:", error.message); return { count: 0, error: error.message }; }
  return { count: (data || []).length, error: null };
}

export async function unassignCompFromDeal(dealId, compId) {
  const { error } = await supabase
    .from("deal_assigned_comps")
    .delete()
    .eq("deal_id", dealId)
    .eq("comp_id", compId);
  if (error) console.error("unassignCompFromDeal error:", error.message);
}

// Returns true if a comp is assigned to a deal (used by the library picker to
// show already-assigned status without re-fetching the full assignment list).
export async function isCompAssignedToDeal(dealId, compId) {
  const { data, error } = await supabase
    .from("deal_assigned_comps")
    .select("id")
    .eq("deal_id", dealId)
    .eq("comp_id", compId)
    .limit(1);
  if (error) return false;
  return (data || []).length > 0;
}

// ── Underwriting versions ────────────────────────────────────────────
//
// A deal's own columns hold whichever underwriting is CURRENT. This returns the
// full history so the deal page can show what an earlier pass said - on
// GoldenMoss, a 5-year hold at a 6.00% exit against a 7-year hold at 7.25%,
// which is the actual argument about the deal rather than a detail.
//
// Returns [] rather than throwing when migration 028 has not run yet, so the
// deal page simply shows no version pill instead of breaking.
export async function loadUwVersions(dealId) {
  if (!dealId) return [];
  const { data, error } = await supabase
    .from("deal_uw_versions")
    .select("*")
    .eq("deal_id", dealId)
    .order("version", { ascending: true });
  if (error) {
    if (!/deal_uw_versions/.test(error.message || "")) {
      console.warn("loadUwVersions:", error.message);
    }
    return [];
  }
  return data || [];
}

// ── Deal notes ───────────────────────────────────────────────────────
//
// What a PERSON decided about the deal, as against deal_data_quality which is
// what the extractor noticed. These outrank the extraction and no loader
// rewrites them, so they are the one place a reader can find out why a figure
// is what it is. Newest first: the last word on a deal is usually the one
// wanted. A missing table returns [] rather than throwing, so a database
// without migration 030 simply shows no notes.
export async function loadDealNotes(dealId) {
  if (!dealId) return [];
  const { data, error } = await supabase
    .from("deal_notes")
    .select("*")
    .eq("deal_id", dealId)
    .order("decided_on", { ascending: false, nullsFirst: false })
    .order("created_at", { ascending: false });
  if (error) {
    if (!/deal_notes/.test(error.message || "")) {
      console.warn("loadDealNotes:", error.message);
    }
    return [];
  }
  return data || [];
}

export async function saveDealNote(dealId, note) {
  const { data, error } = await supabase
    .from("deal_notes")
    .insert([{ deal_id: dealId, ...note }])
    .select();
  if (error) throw error;
  return (data || [])[0];
}

// ── Rent roll helpers ────────────────────────────────────────────────
//
// TWO NAMES FOR THE SAME TWO FACTS. deal_rent_roll carries both suite_sf and
// sf, and both suite_id and unit_label. The extractor writes `sf` and
// `unit_label`; suite_sf and suite_id are populated on 2 of 4,670 rows. Every
// screen that showed a rent roll read the empty pair, so suite sizes rendered
// as a column of dashes while the numbers sat in the next column over.
//
// Normalising here rather than in each screen, because there are six of them
// and they were drifting: dealAnalytics had already grown `num(r.sf) ||
// num(r.suite_sf)` by hand, which is the same fix applied in one place and
// missed in the other five.
export async function loadRentRoll(dealId) {
  const { data, error } = await supabase
    .from("deal_rent_roll")
    .select("*")
    .eq("deal_id", dealId)
    .order("subject_address", { ascending: true })
    // Was suite_id, which is null on every row - so the secondary sort did
    // nothing and suites came back in whatever order the table gave them.
    .order("unit_label", { ascending: true, nullsFirst: false });
  if (error) { console.error("loadRentRoll error:", error.message); return []; }
  return (data || []).map((r) => ({
    ...r,
    suite_sf: r.suite_sf ?? r.sf ?? null,
    suite_id: r.suite_id ?? r.unit_label ?? null,
  }));
}

// THE UPSERT KEY. Not (deal_id, subject_address, tenant, suite_id), which is
// what this used and which never matched a single row: subject_address and
// suite_id are NULL on 4,668 of 4,670 rows, and in Postgres a key containing a
// NULL matches nothing. Re-extracting a deal therefore inserted its whole rent
// roll again, silently, because the database saw no conflict to report.
//
// These are generated columns that coalesce their NULLs away - see migration
// 027 for why the key has to be this wide. Short version: several rows legally
// share a tenant and a suite label (two VACANT spaces of different sizes; an
// option period and its escalation), and source_row is what separates them.
const RENT_ROLL_KEY = "deal_id,dedupe_suite,dedupe_tenant,dedupe_start,dedupe_sf,dedupe_row";
async function rentRollUpsert(rows) {
  return supabase.from("deal_rent_roll")
    .upsert(rows, { onConflict: RENT_ROLL_KEY, ignoreDuplicates: false }).select();
}

export async function saveRentRollRow(dealId, row) {
  const { data, error } = await rentRollUpsert([{ deal_id: dealId, ...row }]);
  if (error) { console.error("saveRentRollRow error:", error.message); return null; }
  return Array.isArray(data) ? (data[0] ?? null) : data;
}

// Bulk version — returns { count, error } so the staging UI can surface
// silent failures (RLS, schema mismatch) instead of just seeing 0 rows.
export async function saveRentRollRows(dealId, rows) {
  if (!rows || rows.length === 0) return { count: 0, error: null };
  const withDeal = rows.map(r => ({ deal_id: dealId, ...r }));
  const { data, error } = await rentRollUpsert(withDeal);
  if (error) { console.error("saveRentRollRows error:", error.message); return { count: 0, error: error.message }; }
  return { count: (data || []).length, error: null };
}

export async function deleteRentRollRow(rowId) {
  const { error } = await supabase.from("deal_rent_roll").delete().eq("id", rowId);
  if (error) console.error("deleteRentRollRow error:", error.message);
}

// ── Global comps library search ─────────────────────────────────────
// Returns comps from the global `comps` table matching filters used by the
// library picker on the deal page. `nearLat` / `nearLng` enable a Haversine
// pre-filter client-side after fetch (Postgres has no built-in haversine
// without PostGIS).
export async function searchGlobalComps({
  type = null,            // 'sale' | 'lease' | null (both)
  query = "",             // free-text address/tenant/buyer match
  msa = null,             // MSA name from msa_definitions; matched against comps.msas[]
  sinceMonths = null,     // only comps with comp_date in the last N months
  excludeIds = null,      // already-assigned comp ids to filter out
  limit = 500,
} = {}) {
  let q = supabase.from("comps").select("*").not("latitude", "is", null).not("longitude", "is", null);
  if (type) q = q.eq("type", type);
  if (msa) q = q.overlaps("msas", [msa]);
  if (sinceMonths) {
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - sinceMonths);
    q = q.gte("comp_date", cutoff.toISOString().slice(0, 10));
  }
  if (query?.trim()) {
    const qLc = `%${query.trim()}%`;
    q = q.or(`address.ilike.${qLc},tenant.ilike.${qLc},buyer.ilike.${qLc},seller.ilike.${qLc},property_name.ilike.${qLc}`);
  }
  q = q.order("comp_date", { ascending: false, nullsFirst: false }).limit(limit);
  const { data, error } = await q;
  if (error) { console.error("searchGlobalComps error:", error.message); return []; }
  let rows = data || [];
  if (excludeIds && excludeIds.length > 0) {
    const skip = new Set(excludeIds);
    rows = rows.filter(r => !skip.has(r.id));
  }
  return rows;
}

// ─── Deal competition (deal_competition) ──────────────────────────────
// One row per deal. Populated by the Kill modal when a deal moves to
// stage='Lost' (or backfilled from the deal page later). deal_id is
// UNIQUE so we upsert on it.

export async function loadCompetition(dealId) {
  const { data, error } = await supabase
    .from("deal_competition")
    .select("*")
    .eq("deal_id", dealId)
    .maybeSingle();
  if (error) { console.warn("loadCompetition:", error.message); return null; }
  return data;
}

// Insert-or-update; deltas (delta_to_winner_psf, delta_pct) are computed
// server-side here from our_final_bid_psf / winning_bid_psf so the caller
// only sends raw inputs.
export async function upsertCompetition(dealId, patch, recordedBy = null) {
  if (!dealId) return { error: "dealId required" };

  const num = (v) => (v == null || v === "" ? null : Number(v));
  const norm = {
    deal_id: dealId,
    num_bidders_estimated: num(patch.num_bidders_estimated),
    our_final_bid_psf:     num(patch.our_final_bid_psf),
    our_final_bid_total:   num(patch.our_final_bid_total),
    made_best_and_final:   patch.made_best_and_final ?? null,
    winning_bid_psf:       num(patch.winning_bid_psf),
    winning_bid_total:     num(patch.winning_bid_total),
    winner_name:           patch.winner_name ?? null,
    lost_reason:           patch.lost_reason ?? null,
    broker_debrief_notes:  patch.broker_debrief_notes ?? null,
    recorded_by:           recordedBy,
    recorded_at:           new Date().toISOString(),
  };

  if (norm.our_final_bid_psf != null && norm.winning_bid_psf != null) {
    norm.delta_to_winner_psf = norm.winning_bid_psf - norm.our_final_bid_psf;
    norm.delta_pct = norm.our_final_bid_psf > 0
      ? (norm.delta_to_winner_psf / norm.our_final_bid_psf) * 100
      : null;
  } else {
    norm.delta_to_winner_psf = null;
    norm.delta_pct = null;
  }

  const existing = await loadCompetition(dealId);
  if (existing) {
    const { data, error } = await supabase
      .from("deal_competition")
      .update(norm)
      .eq("id", existing.id)
      .select()
      .single();
    if (error) { console.error("upsertCompetition (update):", error.message); return { error: error.message }; }
    return { data };
  }
  const { data, error } = await supabase
    .from("deal_competition")
    .insert(norm)
    .select()
    .single();
  if (error) { console.error("upsertCompetition (insert):", error.message); return { error: error.message }; }
  return { data };
}

// ─── Pre-Proxy summaries ──────────────────────────────────────────────
// Aggregate counts for periods before Proxy existed (Apr 2026 and earlier).
// Populated from off-app slides so the Review Pipeline funnel shows 2025
// data without needing 30+ placeholder deal rows.
export async function loadPreProxySummaries() {
  const { data, error } = await supabase
    .from("pre_proxy_summaries")
    .select("*")
    .order("period_start", { ascending: true });
  if (error) { console.warn("loadPreProxySummaries:", error.message); return []; }
  return data || [];
}

// ─── Duplicate detection ──────────────────────────────────────────────
// Nothing stopped the same building being entered twice: "Sunshine
// Industrial" went in on the same day as two rows differing only in the
// market string, and a scan turned up five such pairs across the book.
//
// Two independent tests, because each catches what the other misses:
//   • address — canonical street number + street name, so abbreviations and
//     a missing city don't hide a match
//   • fingerprint — name + square footage + asking price, which catches a
//     genuinely mistyped address on an otherwise identical deal
//
// Returns [{ deal, reason }]. Callers decide what to do with them; nothing
// here blocks on its own.
export async function findDuplicateDeals({ addresses = [], sourceNotes = "", squareFeet = null, askingPrice = null, excludeId = null } = {}) {
  const keys = (addresses || []).map(addressKey).filter(Boolean);
  const name = (parseDealName(sourceNotes) || "").trim().toLowerCase();
  const sf = squareFeet ? Number(squareFeet) : null;
  const ask = askingPrice ? Number(askingPrice) : null;
  if (keys.length === 0 && !name) return [];

  // Deals carry addresses as a JSON array string, so there's no indexable
  // column to query on — pull every deal and compare in memory.
  const { data, error } = await fetchAllRows(() => supabase
    .from("deals")
    .select("id, address, deal_stage, source_notes, square_footage, asking_price, market, created_at")
    .order("created_at", { ascending: false })
    .order("id"));
  if (error) { console.warn("findDuplicateDeals:", error.message); return []; }

  const keySet = new Set(keys);
  const out = [];
  for (const row of data || []) {
    if (excludeId && row.id === excludeId) continue;

    let rowAddrs;
    try {
      const parsed = JSON.parse(row.address ?? "");
      rowAddrs = Array.isArray(parsed) ? parsed : [row.address ?? ""];
    } catch { rowAddrs = [row.address ?? ""]; }

    const hitAddr = rowAddrs.map(addressKey).filter(Boolean).some(k => keySet.has(k));
    if (hitAddr) {
      out.push({ deal: row, reason: "Same address" });
      continue;
    }

    // Fingerprint match needs all three to line up — name alone is far too
    // loose across a book with portfolios and repeated submarket names.
    const rowName = (parseDealName(row.source_notes) || "").trim().toLowerCase();
    if (name && rowName && name === rowName && sf && ask
        && Number(row.square_footage) === sf && Number(row.asking_price) === ask) {
      out.push({ deal: row, reason: "Same name, size and asking price" });
    }
  }
  return out;
}

// Insert a new deal, auto-tagging counties[]/msas[]/market AND running the
// current buy-box rule. Use this instead of a raw supabase.from("deals")
// .insert() so freshly-created deals participate in MSA rollups and get a
// verdict chip immediately.
//
// Refuses by default when the deal looks like one already in the book, and
// hands the matches back so the caller can show them. Pass
// { allowDuplicate: true } to insert anyway — the intent has to be explicit.
export async function insertDeal(deal, { allowDuplicate = false } = {}) {
  if (!allowDuplicate) {
    const duplicates = await findDuplicateDeals({
      addresses: deal?.addresses,
      sourceNotes: deal?.sourceNotes,
      squareFeet: deal?.squareFeet,
      askingPrice: deal?.askingPrice,
    });
    if (duplicates.length > 0) {
      return {
        data: null,
        error: { message: "This looks like a deal Proxy already has.", code: "duplicate_deal" },
        duplicates,
      };
    }
  }

  const payload = dealToDb(deal);
  if (deal && Array.isArray(deal.addresses)) {
    const { counties, msas, marketString } = await _tagAddresses(deal.addresses);
    payload.counties = counties;
    payload.msas = msas;
    if (marketString) payload.market = marketString;
  }
  // Grade after tagging so the evaluator sees the fresh MSA list.
  const grade = await _gradeBuyBox({
    msas: payload.msas,
    market: payload.market,
    product_type: payload.product_type,
    squareFeet: payload.square_footage,
    capRate: payload.cap_rate,
    year_built: payload.year_built,
    clear_height: payload.clear_height,
  });
  if (grade) Object.assign(payload, grade);
  return await supabase.from("deals").insert([payload]).select().single();
}

// Update an existing deal row. Returns { data, error } from Supabase.
// If addresses are being changed, auto-tags counties[]/msas[]/market via
// countyLookup so downstream MSA rollups stay correct without a re-backfill.
// Auto-grading is skipped when the caller passes a manual verdict (deal.
// buyBoxSource === 'manual'), so human overrides survive future edits.
export async function updateDeal(id, deal) {
  const patch = dealToDb(deal);
  if (deal && Array.isArray(deal.addresses)) {
    const { counties, msas, marketString } = await _tagAddresses(deal.addresses);
    patch.counties = counties;
    patch.msas = msas;
    if (marketString) patch.market = marketString;   // overwrite free-text market with auto-derived MSA list
  }
  // Manual override path — accept whatever verdict/reason the caller passed
  // and skip the evaluator. Otherwise re-grade against the current rule.
  if (deal?.buyBoxSource === "manual") {
    patch.buy_box_verdict = deal.buyBoxVerdict ?? "undecided";
    patch.buy_box_source = "manual";
    patch.buy_box_reason = deal.buyBoxReason ?? null;
    patch.buy_box_updated_at = new Date().toISOString();
  } else {
    // Explicit reset-to-auto (caller passed buyBoxSource: 'auto') always
    // re-grades and clears any manual reason. Otherwise, peek at the row: if
    // a human already made a decision, don't clobber it on an auto edit.
    const explicitReset = deal?.buyBoxSource === "auto";
    let existingSource = null;
    if (!explicitReset) {
      const { data: existing } = await supabase
        .from("deals").select("buy_box_source").eq("id", id).maybeSingle();
      existingSource = existing?.buy_box_source;
    }
    if (explicitReset || existingSource !== "manual") {
      const grade = await _gradeBuyBox({
        msas: patch.msas,
        market: patch.market,
        product_type: patch.product_type,
        squareFeet: patch.square_footage,
        capRate: patch.cap_rate,
        year_built: patch.year_built,
        clear_height: patch.clear_height,
      });
      if (grade) {
        Object.assign(patch, grade);
        // Explicit reset drops any manual reason string left behind.
        if (explicitReset) patch.buy_box_reason = grade.buy_box_reason ?? null;
      }
    }
  }
  return await supabase
    .from("deals")
    .update(patch)
    .eq("id", id)
    .select()
    .single();
}

// ─── Text extraction ──────────────────────────────────────────────────────────
// Each branch reads the File as an ArrayBuffer (or plain text) and returns a
// string. Returns null for unsupported types or if extraction throws.

// Helper: read a File object as an ArrayBuffer (Promise-based wrapper)
function readAsArrayBuffer(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = (e) => resolve(e.target.result);
    reader.onerror = () => reject(new Error("FileReader error"));
    reader.readAsArrayBuffer(file);
  });
}

// Helper: read a File object as plain text
function readAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = (e) => resolve(e.target.result);
    reader.onerror = () => reject(new Error("FileReader error"));
    reader.readAsText(file);
  });
}

export async function extractTextFromFile(file, options = {}) {
  try {
    // ── PDF ──────────────────────────────────────────────────────────────────
    if (file.type === "application/pdf") {
      const buffer  = await readAsArrayBuffer(file);
      const pdf     = await pdfjs.getDocument({ data: buffer }).promise;
      const pages   = [];
      for (let i = 1; i <= pdf.numPages; i++) {
        const page    = await pdf.getPage(i);
        const content = await page.getTextContent();
        // Join each text item with a space; separate pages with a newline
        pages.push(content.items.map((item) => item.str).join(" "));
      }
      return pages.join("\n");
    }

    // ── Word (.docx) ─────────────────────────────────────────────────────────
    if (
      file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
      file.type === "application/msword" ||
      file.name.endsWith(".docx")
    ) {
      const buffer = await readAsArrayBuffer(file);
      const result = await mammoth.extractRawText({ arrayBuffer: buffer });
      return result.value;
    }

    // ── Excel / CSV (.xlsx, .xls, .csv) ──────────────────────────────────────
    if (
      file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      file.type === "application/vnd.ms-excel" ||
      file.type === "text/csv" ||
      file.name.endsWith(".xlsx") ||
      file.name.endsWith(".xls") ||
      file.name.endsWith(".csv")
    ) {
      const buffer   = await readAsArrayBuffer(file);
      const workbook = XLSX.read(buffer, { type: "array" });
      const sheetNames = workbook.SheetNames;
      // UW convention: rent-roll data always lives in a sheet literally
      // named "Rent Roll" (or close variants), within the first 250 rows.
      // When the caller flags `prioritizeRentRoll: true` (comp extraction),
      // we (a) put that sheet first so it isn't truncated by the upstream
      // char cap, and (b) cap its row count so context isn't wasted on
      // empty trailing rows.
      const rentRollIdx = sheetNames.findIndex(n => /rent\s*roll/i.test(n));
      let orderedNames = sheetNames;
      if (options.prioritizeRentRoll && rentRollIdx >= 0) {
        orderedNames = [sheetNames[rentRollIdx], ...sheetNames.filter((_, i) => i !== rentRollIdx)];
      }
      const sheets = orderedNames.map((name) => {
        const ws = workbook.Sheets[name];
        let csv = XLSX.utils.sheet_to_csv(ws);
        if (options.prioritizeRentRoll && /rent\s*roll/i.test(name)) {
          // Keep header + first 250 data rows
          const rows = csv.split("\n");
          if (rows.length > 251) csv = rows.slice(0, 251).join("\n");
        }
        return `--- Sheet: ${name} ---\n${csv}`;
      });
      return sheets.join("\n\n");
    }

    // ── Plain text ────────────────────────────────────────────────────────────
    if (file.type === "text/plain" || file.name.endsWith(".txt")) {
      return await readAsText(file);
    }

    // Unsupported file type — caller will leave extracted_text as null
    return null;

  } catch (err) {
    // Never block an upload because extraction failed — log and continue
    console.error(`extractTextFromFile error for "${file.name}":`, err.message);
    return null;
  }
}

// ─── Document helpers ─────────────────────────────────────────────────────────

// Upload a File object to Storage, extract its text, then record everything in
// the documents table. Returns the inserted document row. Throws on error.
export async function uploadDocument(dealId, file) {
  console.log("uploadDocument called:", dealId, file.name);

  const path = `${dealId}/${file.name}`;

  // Try a fresh INSERT first. The storage.objects RLS policy on this
  // bucket allows INSERT but not UPDATE for anon, so `upsert: true`
  // (which becomes UPDATE when the path already exists) gets rejected
  // with a misleading "new row violates row-level security policy"
  // error. If the upload fails because the object already exists, we
  // know the file is already in storage and skip to the DB insert.
  const { error: uploadError } = await supabase.storage
    .from("deal-documents")
    .upload(path, file, { upsert: false });

  if (uploadError) {
    const msg = (uploadError.message || "").toLowerCase();
    const status = String(uploadError.statusCode || uploadError.status || "");
    const alreadyExists =
      status === "409" ||
      msg.includes("duplicate") ||
      msg.includes("already exists") ||
      msg.includes("resource already exists");
    if (!alreadyExists) throw uploadError;
    console.log("File already in storage, reusing existing object:", path);
  }

  // Extract text so the AI chat can reference document contents. Falls back
  // to empty string so the row is always inserted even if extraction fails.
  // Cap the stored text at 500 KB — large xlsx files (especially UW models
  // with many sheets) can produce 20+ MB of CSV via sheet_to_csv, which
  // blows past Postgres statement timeouts when INSERTed and shows up as a
  // misleading 403/RLS error in the browser. 500 KB is plenty for the AI
  // chat to grok the document; the original file is still in Storage if a
  // user needs the full thing.
  const MAX_EXTRACTED_TEXT_BYTES = 500_000;
  let extractedText = "";
  try {
    const result = await extractTextFromFile(file);
    extractedText = result ?? "";
    if (extractedText.length > MAX_EXTRACTED_TEXT_BYTES) {
      extractedText = extractedText.slice(0, MAX_EXTRACTED_TEXT_BYTES) + "\n\n[…truncated for storage…]";
    }
  } catch (e) {
    console.warn("Text extraction failed for", file.name, e);
  }

  const { data, error: dbError } = await supabase
    .from("documents")
    .insert([{
      deal_id:        dealId,
      file_name:      file.name,
      file_type:      file.type || file.name.split(".").pop(),
      storage_path:   path,
      extracted_text: extractedText,
      file_size:      file.size,
    }])
    .select()
    .single();

  if (dbError) throw dbError;

  console.log("Document saved to DB:", data);
  return data;
}

export async function loadDocuments(dealId) {
  const { data, error } = await supabase
    .from("documents")
    .select("*")
    .eq("deal_id", dealId)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return data || [];
}

// Delete the file from Storage first, then remove the DB row.
export async function deleteDocument(doc) {
  const { error: storageError } = await supabase.storage
    .from("deal-documents")
    .remove([doc.storage_path]);

  if (storageError) console.error("deleteDocument storage error:", storageError.message);

  const { error: dbError } = await supabase
    .from("documents")
    .delete()
    .eq("id", doc.id);

  if (dbError) console.error("deleteDocument db error:", dbError.message);
}

// Generate a short-lived signed URL so the browser can download/view the file.
export async function getDocumentUrl(storagePath) {
  const { data, error } = await supabase.storage
    .from("deal-documents")
    .createSignedUrl(storagePath, 3600); // 1-hour expiry

  if (error) { console.error("getDocumentUrl error:", error.message); return null; }
  return data.signedUrl;
}

// Signed URLs for many files at once, as { storagePath: url }. Signing one
// file per request for every deal's photo fired hundreds of requests the moment
// the pipeline loaded, Supabase refused the excess ("too many connections"),
// and those thumbnails came up blank. createSignedUrls signs a list in one
// request; lists are sent SIGN_BATCH at a time, one after another.
const SIGN_BATCH = 100;
export async function getDocumentUrls(storagePaths) {
  const paths = [...new Set((storagePaths || []).filter(Boolean))];
  const urls = {};
  for (let i = 0; i < paths.length; i += SIGN_BATCH) {
    const { data, error } = await supabase.storage
      .from("deal-documents")
      .createSignedUrls(paths.slice(i, i + SIGN_BATCH), 3600);
    if (error) { console.error("getDocumentUrls error:", error.message); continue; }
    for (const row of data || []) if (row.signedUrl && row.path) urls[row.path] = row.signedUrl;
  }
  return urls;
}

// ═══════════════════════════════════════════════════════════════════════════════
// DATABASE TAB — Properties, Contacts, Outreach
// ═══════════════════════════════════════════════════════════════════════════════

// --- Properties ---
// Supabase caps a single .select() at 1000 rows. This helper pages through
// .range() until the table is exhausted so callers always get every row.
async function fetchAll(table, { orderColumn = 'created_at', ascending = false } = {}) {
  const { data, error } = await fetchAllRows(() =>
    supabase.from(table).select('*').order(orderColumn, { ascending }).order('id'));
  if (error) throw error;
  return data;
}

// Every row a query matches, as { data, error }. PostgREST caps a response at
// 1000 rows WHATEVER .limit() asks for - .limit(20000) quietly returns 1000 -
// so this pages with .range() until a short page. `build` returns a fresh query
// each call, and must .order() on something unique (end with .order("id")):
// rows that tie can otherwise land on two pages, or none.
export async function fetchAllRows(build, pageSize = 1000) {
  let all = [];
  for (let from = 0; ; from += pageSize) {
    const { data, error } = await build().range(from, from + pageSize - 1);
    if (error) return { data: all, error };
    all = all.concat(data || []);
    if (!data || data.length < pageSize) return { data: all, error: null };
  }
}

export async function loadProperties() {
  return fetchAll('properties', { orderColumn: 'created_at', ascending: false });
}

// A Sourcing property's pin is saved, so it goes through the strict geocoder:
// state enforced, house number and street confirmed, footprint only. This used
// to carry its own copy of the old country-only lookup.
async function geocodeForSave(address, city, state) {
  const fullAddr = [address, city, state].filter(Boolean).join(", ");
  if (!fullAddr.trim()) return null;
  const c = await geocodeAddress(fullAddr);
  return c ? { latitude: c.lat, longitude: c.lng } : null;
}

export async function upsertProperty(prop) {
  // Delete blocklist check — if this address was previously deleted from
  // Sourcing, don't bring it back via a re-upload. Manually DELETE from
  // deleted_property_addresses to allow re-import.
  const { data: blocked } = await supabase
    .from('deleted_property_addresses')
    .select('id')
    .eq('address', prop.address)
    .eq('city', prop.city || '')
    .eq('state', prop.state || '')
    .limit(1);
  if (blocked && blocked.length > 0) {
    return { data: null, action: 'skipped_deleted' };
  }

  // Auto-geocode if no coordinates provided
  if (!prop.latitude && prop.address) {
    const coords = await geocodeForSave(prop.address, prop.city, prop.state);
    if (coords) { prop.latitude = coords.latitude; prop.longitude = coords.longitude; }
  }

  // Auto-tag county + MSA(s) on every upsert. Uses full "address, city, state"
  // for the geocode lookup so we get the right admin_area_level_2 back.
  const fullAddr = [prop.address, prop.city, prop.state].filter(Boolean).join(", ");
  if (fullAddr) {
    const { counties, msas, marketString } = await _tagAddresses([fullAddr]);
    prop.counties = counties;
    prop.msas = msas;
    if (marketString) prop.market = marketString;
  }

  const { data: existing } = await supabase
    .from('properties')
    .select('id, buy_box_source')
    .eq('address', prop.address)
    .eq('city', prop.city || '')
    .eq('state', prop.state || '')
    .limit(1);

  // Auto-grade unless a human already made a manual call on the existing row.
  // Insert path always grades — there's nothing to override yet.
  const canGrade = !(existing && existing.length > 0 && existing[0].buy_box_source === "manual");
  if (canGrade) {
    const grade = await _gradeBuyBox({
      msas: prop.msas,
      market: prop.market,
      product_type: prop.product_type,
      size_sf: prop.size_sf,
      building_sf: prop.building_sf,
      year_built: prop.year_built,
      clear_height: prop.clear_height,
    });
    if (grade) Object.assign(prop, grade);
  }

  if (existing && existing.length > 0) {
    const { data, error } = await supabase
      .from('properties')
      .update({ ...prop, updated_at: new Date().toISOString() })
      .eq('id', existing[0].id)
      .select()
      .single();
    if (error) throw error;
    return { data, action: 'updated' };
  } else {
    const { data, error } = await supabase
      .from('properties')
      .insert(prop)
      .select()
      .single();
    if (error) throw error;
    return { data, action: 'created' };
  }
}

export async function updateProperty(id, updates) {
  // Fetch the existing row once — we need it for the address backfill AND for
  // the manual-override check. Skipping this on payloads that touch neither
  // address nor grading fields would save a round-trip but the extra call is
  // cheap enough (properties are rarely edited in tight loops).
  const { data: existing } = await supabase
    .from('properties')
    .select('address, city, state, product_type, size_sf, building_sf, year_built, clear_height, msas, market, buy_box_source')
    .eq('id', id)
    .maybeSingle();

  // If the update touches address / city / state, re-derive counties + MSAs
  // + market so the row stays in the correct MSA rollups.
  if (updates && (updates.address !== undefined || updates.city !== undefined || updates.state !== undefined)) {
    const base = { ...(existing || {}), ...updates };
    const fullAddr = [base.address, base.city, base.state].filter(Boolean).join(", ");
    if (fullAddr) {
      const { counties, msas, marketString } = await _tagAddresses([fullAddr]);
      updates = { ...updates, counties, msas };
      if (marketString) updates.market = marketString;
    }
  }

  // Manual override survives future edits; auto-graded rows re-grade whenever
  // any dimension the rule cares about changes.
  const source = updates?.buy_box_source ?? existing?.buy_box_source;
  if (source !== "manual") {
    const merged = { ...(existing || {}), ...updates };
    const grade = await _gradeBuyBox({
      msas: merged.msas,
      market: merged.market,
      product_type: merged.product_type,
      size_sf: merged.size_sf,
      building_sf: merged.building_sf,
      year_built: merged.year_built,
      clear_height: merged.clear_height,
    });
    if (grade) updates = { ...updates, ...grade };
  }

  const { data, error } = await supabase
    .from('properties')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteProperties(ids, deletedBy = null) {
  if (!ids || ids.length === 0) return;
  // Fetch the address tuples first so we can record them in the kill list.
  // This is what stops re-imports from bringing the same property back.
  const { data: rows } = await supabase
    .from('properties')
    .select('address, city, state')
    .in('id', ids);

  const { error } = await supabase
    .from('properties')
    .delete()
    .in('id', ids);
  if (error) {
    console.error('deleteProperties error:', error);
    const detail = [error.message, error.details, error.hint].filter(Boolean).join(' — ');
    throw new Error(detail || error.code || 'Unknown delete error');
  }

  // Record each deleted address so future CoStar uploads skip it.
  if (rows && rows.length > 0) {
    const blocklistRows = rows
      .filter(r => r.address)
      .map(r => ({ address: r.address, city: r.city || '', state: r.state || '', deleted_by: deletedBy }));
    if (blocklistRows.length > 0) {
      const { error: blErr } = await supabase
        .from('deleted_property_addresses')
        .upsert(blocklistRows, { onConflict: 'address,city,state', ignoreDuplicates: true });
      if (blErr) console.error('blocklist insert error (non-fatal):', blErr.message);
    }
  }
}

// --- Contacts ---
export async function loadContacts() {
  return fetchAll('contacts', { orderColumn: 'created_at', ascending: false });
}

export { verifyEmail };

export async function upsertContact(contact) {
  // Auto-verify emails before saving
  const verified = await autoVerifyContact(contact);

  if (verified.id) {
    const { data, error } = await supabase
      .from('contacts')
      .update(verified)
      .eq('id', verified.id)
      .select()
      .single();
    if (error) throw error;
    return data;
  } else {
    const { data, error } = await supabase
      .from('contacts')
      .insert(verified)
      .select()
      .single();
    if (error) throw error;
    return data;
  }
}

export async function deleteContact(id) {
  const { error } = await supabase
    .from('contacts')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

// --- Outreach Log ---
export async function loadOutreachLog() {
  return fetchAll('outreach_log', { orderColumn: 'sent_at', ascending: false });
}

export async function createOutreachEntry(entry) {
  const { data, error } = await supabase
    .from('outreach_log')
    .insert(entry)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateOutreachEntry(id, updates) {
  const { data, error } = await supabase
    .from('outreach_log')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// --- Institutional Owners ---
export async function loadInstitutionalOwners() {
  const { data, error } = await supabase
    .from('institutional_owners')
    .select('*')
    .order('name');
  if (error) throw error;
  return data || [];
}

// --- App Settings ---
export async function loadAppSettings() {
  const { data, error } = await supabase
    .from('app_settings')
    .select('*');
  if (error) throw error;
  const settings = {};
  (data || []).forEach(row => { settings[row.key] = row.value; });
  return settings;
}

export async function updateAppSetting(key, value) {
  const { error } = await supabase
    .from('app_settings')
    .upsert({ key, value: String(value) });
  if (error) throw error;
}

// ─── PDF → images ─────────────────────────────────────────────────────────────
// Renders each page of a PDF to a JPEG via canvas and returns an array of
// raw base64 strings (no data: prefix). Used by the OM extraction flow so
// we can send pages as image content blocks instead of a raw PDF document,
// which avoids browser payload limits on direct Anthropic API calls.

export async function pdfToImages(file, maxPages = 15) {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
  const pagesToRender = Math.min(pdf.numPages, maxPages);
  const images = [];

  for (let i = 1; i <= pagesToRender; i++) {
    const page     = await pdf.getPage(i);
    // 1.5× scale gives readable text without ballooning the payload
    const viewport = page.getViewport({ scale: 1.5 });
    const canvas   = document.createElement("canvas");
    canvas.width   = viewport.width;
    canvas.height  = viewport.height;
    const ctx      = canvas.getContext("2d");
    await page.render({ canvasContext: ctx, viewport }).promise;
    // JPEG at 70% quality — good enough for Claude to read text
    const dataUrl  = canvas.toDataURL("image/jpeg", 0.7);
    images.push(dataUrl.split(",")[1]); // strip "data:image/jpeg;base64,"
  }

  return { images, totalPages: pdf.numPages, renderedPages: pagesToRender };
}

// ─── PDF vision helper ────────────────────────────────────────────────────────
// Downloads a PDF from Supabase Storage, renders its pages to JPEG via canvas,
// and returns an array of Claude API image content blocks ready to include in a
// messages array. Throws on storage or render errors.

export async function getPdfPageImages(storagePath, maxPages = 50) {
  const { data: blob, error } = await supabase.storage
    .from("deal-documents")
    .download(storagePath);
  if (error) throw error;

  const file = new File([blob], storagePath.split("/").pop() || "document.pdf", {
    type: "application/pdf",
  });

  // pdfToImages returns { images, totalPages, renderedPages } — destructure images
  const { images } = await pdfToImages(file, maxPages);

  return images.map((b64) => ({
    type: "image",
    source: {
      type: "base64",
      media_type: "image/jpeg",
      data: b64,
    },
  }));
}

// ─── Map boundaries (saved submarkets) ────────────────────────────────────────
// Backed by migrations/018_map_boundaries.sql. Until that migration is run
// these report `installed: false` instead of throwing, so the map's
// draw / filter / export flow keeps working without the save feature.

function isMissingBoundaryTable(error) {
  if (!error) return false;
  if (error.code === '42P01' || error.code === 'PGRST205') return true;
  const msg = error.message || '';
  return /map_boundaries/i.test(msg) && /(does not exist|could not find)/i.test(msg);
}

export async function loadMapBoundaries() {
  const { data, error } = await supabase
    .from('map_boundaries')
    .select('*')
    .order('name');
  if (error) {
    if (isMissingBoundaryTable(error)) return { installed: false, boundaries: [] };
    throw error;
  }
  return {
    installed: true,
    boundaries: (data || []).map(r => ({
      id: r.id, name: r.name, coords: r.coords,
      createdBy: r.created_by, createdAt: r.created_at,
    })),
  };
}

// Saves by name — re-saving an existing name overwrites its shape, which is
// what you want when nudging a submarket border rather than making a new one.
export async function saveMapBoundary(name, coords, createdBy) {
  const trimmed = (name || '').trim();
  if (!trimmed) throw new Error('Boundary needs a name.');
  if (!Array.isArray(coords) || coords.length < 3) throw new Error('Boundary needs at least 3 points.');

  const { data: existing, error: findErr } = await supabase
    .from('map_boundaries')
    .select('id')
    .ilike('name', trimmed)
    .maybeSingle();
  if (findErr) {
    if (isMissingBoundaryTable(findErr)) return { installed: false };
    throw findErr;
  }

  if (existing) {
    const { error } = await supabase
      .from('map_boundaries')
      .update({ coords, created_by: createdBy || null })
      .eq('id', existing.id);
    if (error) throw error;
    return { installed: true, id: existing.id, replaced: true };
  }

  const { data, error } = await supabase
    .from('map_boundaries')
    .insert({ name: trimmed, coords, created_by: createdBy || null })
    .select('id')
    .single();
  if (error) {
    if (isMissingBoundaryTable(error)) return { installed: false };
    throw error;
  }
  return { installed: true, id: data.id, replaced: false };
}

export async function deleteMapBoundary(id) {
  const { error } = await supabase.from('map_boundaries').delete().eq('id', id);
  if (error) throw error;
}

// ─── Address dossier ──────────────────────────────────────────────────────
// Everything recorded against one street address, for the map's property
// page. The page is keyed by address rather than by record because an
// address is the durable identity — it can be a sourcing property today, a
// deal next quarter, and a comp after it trades — and all of that history
// belongs on one page.
//
// Sourcing properties are no longer loaded for map pins, so this fetches the
// single row on demand instead of keeping ~1,750 in memory.
export async function loadAddressDossier(street) {
  const key = (street || "").trim();
  if (!key) return { property: null, contacts: [], outreach: [] };

  // `address` on properties is the street only; deals store the full string,
  // so callers pass the street portion. Prefix match tolerates a trailing
  // suite or unit that one side records and the other does not.
  const { data: props, error } = await supabase
    .from("properties")
    .select("*")
    .ilike("address", `${key}%`)
    .limit(1);
  if (error) {
    console.error("loadAddressDossier:", error.message);
    return { property: null, contacts: [], outreach: [] };
  }
  const property = (props || [])[0] || null;
  if (!property) return { property: null, contacts: [], outreach: [] };

  const [{ data: contacts }, { data: outreach }] = await Promise.all([
    supabase.from("contacts").select("*").eq("property_id", property.id),
    supabase.from("outreach_log").select("*").eq("property_id", property.id)
      .order("sent_at", { ascending: false }),
  ]);

  return { property, contacts: contacts || [], outreach: outreach || [] };
}


// ═══════════════════════════════════════════════════════════════════════
// Comp database (migrations 022-024)
//
// These read the tables the Phase 3 load fills from the master comp
// database. Everything here is READ-ONLY by design: the master is the
// source of truth, a reload replaces these rows wholesale, and anything
// written here would be silently discarded on the next load. The one
// exception is `deal_handpicked_comps`, which exists precisely so that a
// judgement made in the app survives a reload.
// ═══════════════════════════════════════════════════════════════════════

// An industrial rent in these markets clears somewhere between about $3 and
// $60 a foot, which is the band the comp gate already uses. Applying it here
// too is not second-guessing the data - it is refusing to average a figure
// that cannot be a rent. 15 of the 4,422 market-rent rows fall outside it,
// and one of them, a $2,500/SF ARGUS figure on Greens Road Business Park,
// moved that deal's average market rent from $10.50 to $88.27 on its own.
//
// The rows are not hidden or deleted; they are excluded from AVERAGES. A
// figure that cannot be a rent should not set the rent, but it is still
// evidence of a misread and belongs in the data-quality trail.
export const RENT_PSF_FLOOR = 3;
export const RENT_PSF_CEILING = 60;

export function plausibleRent(v) {
  const n = Number(v);
  return Number.isFinite(n) && n >= RENT_PSF_FLOOR && n <= RENT_PSF_CEILING;
}

// Market-rent assumptions: the three sides of the four-way comparison that
// are not the signed rent roll. `assumption_type` distinguishes them -
// 'Broker MLA' (what the OM claimed), 'UW MLA' (where we underwrote),
// 'PPUSA MLA' (our standard assumption).
export async function loadMarketRent(dealId) {
  const { data, error } = await supabase
    .from("market_rent")
    .select("*")
    .eq("deal_id", dealId)
    .order("assumption_type", { ascending: true })
    .order("unit_id", { ascending: true });
  if (error) { console.error("loadMarketRent error:", error.message); return []; }
  return data || [];
}

// Itemised operating expenses. Several bases coexist on purpose - a T12, a
// budget and our own underwriting are three different claims about the same
// property, and blending them hides the disagreement that matters.
export async function loadOpexLines(dealId) {
  const { data, error } = await supabase
    .from("opex_lines")
    .select("*")
    .eq("deal_id", dealId)
    .order("basis", { ascending: true })
    .order("amount_annual", { ascending: false });
  if (error) { console.error("loadOpexLines error:", error.message); return []; }
  return data || [];
}

// The extraction's audit trail for one deal: what was gated, flagged or
// excluded, and why. Drives the data-quality panel.
export async function loadDataQuality(dealId, { severities = null } = {}) {
  let q = supabase.from("deal_data_quality").select("*").eq("deal_id", dealId);
  if (severities && severities.length) q = q.in("severity", severities);
  const { data, error } = await q.order("severity", { ascending: true });
  if (error) { console.error("loadDataQuality error:", error.message); return []; }
  return data || [];
}

// ── the OpEx matrix: category down, basis across ──────────────────────
// Totals are excluded from the body and returned separately, because a
// stated total and the lines it totals must never be added together - that
// mistake doubled every deal's expenses once already.
export function buildOpexMatrix(lines, totalRsf) {
  const bases = [];
  const cats = [];
  const cell = {};
  const statedTotal = {};

  (lines || []).forEach((l) => {
    const basis = l.basis || "Unknown";
    if (!bases.includes(basis)) bases.push(basis);
    if (l.is_subtotal || l.category === "Total") {
      statedTotal[basis] = l.amount_annual;
      return;
    }
    const cat = l.category || "Other";
    if (!cats.includes(cat)) cats.push(cat);
    const k = cat + "||" + basis;
    cell[k] = (cell[k] || 0) + (Number(l.amount_annual) || 0);
  });

  const computed = {};
  bases.forEach((b) => {
    computed[b] = cats.reduce((sum, c) => sum + (cell[c + "||" + b] || 0), 0);
  });

  // Where two bases disagree by more than 15% on the same category, the
  // cell is worth looking at rather than reading past.
  const disagrees = {};
  cats.forEach((c) => {
    const vals = bases.map((b) => cell[c + "||" + b]).filter((v) => v > 0);
    if (vals.length < 2) return;
    const lo = Math.min(...vals), hi = Math.max(...vals);
    if (lo > 0 && (hi - lo) / lo > 0.15) disagrees[c] = true;
  });

  return {
    bases, categories: cats, cell, statedTotal, computed, disagrees,
    psf: (v) => (totalRsf ? v / totalRsf : null),
  };
}

// ── the four-way rent comparison ──────────────────────────────────────
// The question a deal page exists to answer: what the broker said, where we
// underwrote, what the rent roll actually shows signed, and what nearby
// comps have signed. Returned as four figures plus the spread.
export function buildFourWayRent({ rentRoll = [], marketRent = [], nearbyComps = [] } = {}) {
  const avg = (xs) => {
    const v = xs.filter((x) => Number.isFinite(x) && x > 0);
    return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null;
  };
  // weighted by area, because a 40,000 ft lease says more about a building
  // than a 900 ft one
  const wavg = (rows, rate, size) => {
    let num = 0, den = 0;
    rows.forEach((r) => {
      const v = Number(r[rate]), sf = Number(r[size]);
      if (Number.isFinite(v) && v > 0 && Number.isFinite(sf) && sf > 0) {
        num += v * sf; den += sf;
      }
    });
    return den ? num / den : null;
  };

  const byType = (t) => marketRent.filter(
    (m) => m.assumption_type === t && plausibleRent(m.rent_psf));
  const signed = rentRoll.filter((r) => !r.is_vacant);

  return {
    brokerSaid: avg(byType("Broker MLA").map((m) => Number(m.rent_psf))),
    weUnderwrote: avg([...byType("UW MLA"), ...byType("PPUSA MLA")]
      .map((m) => Number(m.rent_psf))),
    rentRollSigned: wavg(signed, "lease_rate", "sf"),
    nearbySigned: wavg(nearbyComps, "lease_rate", "suite_sf"),
    signedCount: signed.length,
    nearbyCount: nearbyComps.length,
  };
}

// ── mark to market, per suite ─────────────────────────────────────────
// Sorted by upside so the suites that drive the deal surface first.
export function buildMarkToMarket(rentRoll, marketRent) {
  const mktByUnit = {};
  (marketRent || []).forEach((m) => {
    if (m.unit_id && plausibleRent(m.rent_psf)) mktByUnit[m.unit_id] = Number(m.rent_psf);
  });
  const dealLevel = (marketRent || [])
    .filter((m) => m.scope === "deal" && plausibleRent(m.rent_psf))
    .map((m) => Number(m.rent_psf));
  const fallback = dealLevel.length
    ? dealLevel.reduce((a, b) => a + b, 0) / dealLevel.length : null;

  return (rentRoll || [])
    .map((r) => {
      const inPlace = Number(r.lease_rate) || Number(r.rent_nnn_psf) || null;
      const market = mktByUnit[r.unit_id] ?? fallback;
      const gap = inPlace && market ? market - inPlace : null;
      return {
        ...r,
        inPlace, market, gap,
        gapPct: gap && inPlace ? gap / inPlace : null,
        annualUpside: gap && r.sf ? gap * Number(r.sf) : null,
      };
    })
    .sort((a, b) => (b.annualUpside || 0) - (a.annualUpside || 0));
}

// ── nearby comps, with the radius toggle ──────────────────────────────
// Postgres has no haversine without PostGIS, so the box is done in SQL and
// the circle in JS - the box is cheap and cuts the rows enormously.
const MILES_PER_DEG_LAT = 69.0;

export async function loadNearbyComps(lat, lng, radiusMiles = 1, {
  type = "lease", productType = null, sizeBand = null,
  sinceYear = null, limit = 400,
} = {}) {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return [];
  const dLat = radiusMiles / MILES_PER_DEG_LAT;
  const dLng = radiusMiles / (MILES_PER_DEG_LAT * Math.cos((lat * Math.PI) / 180) || 1);

  let q = supabase.from("comps").select("*")
    .gte("latitude", lat - dLat).lte("latitude", lat + dLat)
    .gte("longitude", lng - dLng).lte("longitude", lng + dLng);
  if (type) q = q.eq("type", type);
  if (sizeBand) q = q.eq("sf_band", sizeBand);
  if (sinceYear) q = q.gte("comp_date", `${sinceYear}-01-01`);
  const { data, error } = await q.limit(limit);
  if (error) { console.error("loadNearbyComps error:", error.message); return []; }

  return (data || [])
    .map((c) => ({ ...c, distanceMiles: haversineMiles(lat, lng, c.latitude, c.longitude) }))
    .filter((c) => c.distanceMiles <= radiusMiles)
    .filter((c) => !productType || (c.property_name || "").toLowerCase().includes(productType.toLowerCase()))
    .sort((a, b) => a.distanceMiles - b.distanceMiles);
}

export function haversineMiles(lat1, lng1, lat2, lng2) {
  if (![lat1, lng1, lat2, lng2].every(Number.isFinite)) return Infinity;
  const R = 3958.8, rad = (d) => (d * Math.PI) / 180;
  const dLat = rad(lat2 - lat1), dLng = rad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
}

// ── hand-picked comps ─────────────────────────────────────────────────
// The one thing here that Proxy writes and a reload respects. Badged in the
// UI as judgement rather than sourced evidence, because that is what it is.
export async function loadHandpickedComps(dealId) {
  const { data, error } = await supabase
    .from("deal_handpicked_comps")
    .select("*, comps(*)")
    .eq("deal_id", dealId);
  if (error) { console.error("loadHandpickedComps error:", error.message); return []; }
  return data || [];
}

export async function addHandpickedComp(dealId, compId, addedBy = null, notes = null) {
  const { data, error } = await supabase
    .from("deal_handpicked_comps")
    .upsert([{ deal_id: dealId, comp_id: compId, added_by: addedBy, notes }],
            { onConflict: "deal_id,comp_id", ignoreDuplicates: true })
    .select();
  if (error) { console.error("addHandpickedComp error:", error.message); return null; }
  return data;
}

export async function removeHandpickedComp(dealId, compId) {
  const { error } = await supabase.from("deal_handpicked_comps")
    .delete().eq("deal_id", dealId).eq("comp_id", compId);
  if (error) console.error("removeHandpickedComp error:", error.message);
}

// ── lease expiry schedule ─────────────────────────────────────────────
// SF and income rolling by year, which is when the mark-to-market can
// actually be captured.
export function buildExpirySchedule(rentRoll, yearsAhead = 10) {
  const now = new Date().getFullYear();
  const rows = [];
  for (let y = now; y < now + yearsAhead; y += 1) {
    rows.push({ year: y, sf: 0, income: 0, tenants: [] });
  }
  (rentRoll || []).forEach((r) => {
    if (r.is_vacant || !r.lease_end) return;
    const y = Number(String(r.lease_end).slice(0, 4));
    const slot = rows.find((x) => x.year === y);
    if (!slot) return;
    const sf = Number(r.sf) || 0;
    slot.sf += sf;
    slot.income += sf * (Number(r.lease_rate) || 0);
    if (r.tenant) slot.tenants.push(r.tenant);
  });
  return rows;
}


// ── pipeline metrics, for every deal at once ──────────────────────────
// The table view needs in-place rent, market rent, mark-to-market, OpEx and
// an evidence count for ~150 deals. Asked per deal that is 600 round trips;
// asked this way it is four, because the aggregation is cheap client-side and
// the rows are small. PostgREST caps a response at 1,000 rows, so each table
// is paged until it comes back short.
async function pageAll(table, select) {
  const out = [];
  const size = 1000;
  for (let from = 0; ; from += size) {
    const { data, error } = await supabase
      .from(table).select(select).range(from, from + size - 1);
    if (error) { console.error(`pageAll ${table}:`, error.message); break; }
    out.push(...(data || []));
    if (!data || data.length < size) break;
  }
  return out;
}

export async function loadPipelineMetrics() {
  const [rr, mr, ox, comps] = await Promise.all([
    pageAll("deal_rent_roll", "deal_id,sf,lease_rate,is_vacant,lease_end"),
    pageAll("market_rent", "deal_id,rent_psf,assumption_type,scope"),
    pageAll("opex_lines", "deal_id,amount_annual,basis,is_subtotal,category"),
    pageAll("comps", "comp_db_deal_id"),
  ]);

  const m = {};
  const get = (id) => (m[id] = m[id] || {
    sfLeased: 0, sfVacant: 0, rentNum: 0, rentDen: 0, leases: 0, vacant: 0,
    mktSum: 0, mktN: 0, opexByBasis: {}, comps: 0, rollingSf: 0,
  });

  const thisYear = new Date().getFullYear();
  rr.forEach((r) => {
    const d = get(r.deal_id);
    const sf = Number(r.sf) || 0;
    if (r.is_vacant) { d.sfVacant += sf; d.vacant += 1; return; }
    d.sfLeased += sf; d.leases += 1;
    const rate = Number(r.lease_rate) || 0;
    if (rate > 0 && sf > 0) { d.rentNum += rate * sf; d.rentDen += sf; }
    const y = Number(String(r.lease_end || "").slice(0, 4));
    if (y && y <= thisYear + 2) d.rollingSf += sf;
  });

  mr.forEach((r) => {
    const d = get(r.deal_id);
    if (!plausibleRent(r.rent_psf)) { d.mktRefused = (d.mktRefused || 0) + 1; return; }
    d.mktSum += Number(r.rent_psf); d.mktN += 1;
  });

  ox.forEach((r) => {
    // A stated total and the lines it totals must never be added together.
    if (r.is_subtotal || r.category === "Total") return;
    const d = get(r.deal_id);
    const b = r.basis || "Unknown";
    d.opexByBasis[b] = (d.opexByBasis[b] || 0) + (Number(r.amount_annual) || 0);
  });

  const compsByKey = {};
  comps.forEach((c) => {
    if (!c.comp_db_deal_id) return;
    compsByKey[c.comp_db_deal_id] = (compsByKey[c.comp_db_deal_id] || 0) + 1;
  });

  const out = {};
  Object.entries(m).forEach(([dealId, d]) => {
    const inPlace = d.rentDen ? d.rentNum / d.rentDen : null;
    const market = d.mktN ? d.mktSum / d.mktN : null;
    const bases = Object.keys(d.opexByBasis);
    const opex = bases.length ? d.opexByBasis[bases[0]] : null;
    out[dealId] = {
      inPlace, market,
      gap: inPlace && market ? market - inPlace : null,
      upside: inPlace && market && d.sfLeased ? (market - inPlace) * d.sfLeased : null,
      leases: d.leases, vacantUnits: d.vacant,
      sfLeased: d.sfLeased, sfVacant: d.sfVacant,
      occupancy: (d.sfLeased + d.sfVacant) ? d.sfLeased / (d.sfLeased + d.sfVacant) : null,
      opexAnnual: opex, opexBasis: bases[0] || null,
      rollingSf: d.rollingSf,
      mktRefused: d.mktRefused || 0,
    };
  });
  return { byDealId: out, compsByKey };
}
